import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { LayoutGrid, List, Filter, Sparkles, RefreshCw, Signal, Trash2, GripVertical, Megaphone, AlertTriangle, Clock } from "lucide-react";
import api, { freshnessLabel, formatApiError, taskDueInfo } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const STAGE_COLORS = {
  non_assegnato: "border-l-faint",
  da_contattare: "border-l-brand",
  non_qualificato: "border-l-[#B5B4AF]",
  qualificato: "border-l-brand-dark",
  fissato: "border-l-brand",
};

function TaskBadge({ task }) {
  const info = taskDueInfo(task?.due_at);
  if (!info) return null;
  return (
    <span
      data-testid="task-badge"
      title={task.note || "Task"}
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${info.urgent ? "bg-[#FFE5E5] text-destructive animate-pulse" : "bg-brand-soft text-brand-dark"}`}
    >
      <Clock className="w-3 h-3" strokeWidth={1.5} /> {info.overdue ? "Task scaduto" : `Task ${info.label}`}
    </span>
  );
}

function Badges({ o }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {o.number === 1 && !o.is_reactivation && (
        <span className="inline-flex items-center gap-1 bg-brand-soft text-brand-dark rounded-full px-2 py-0.5 text-[11px] font-medium">
          <Sparkles className="w-3 h-3" strokeWidth={1.5} /> Nuova
        </span>
      )}
      {o.is_reactivation && (
        <span className="inline-flex items-center gap-1 bg-brand-soft text-brand-dark rounded-full px-2 py-0.5 text-[11px] font-medium">
          <RefreshCw className="w-3 h-3" strokeWidth={1.5} /> Riattivata #{o.number}
        </span>
      )}
      {o.has_new_signal && (
        <span className="inline-flex items-center gap-1 bg-[#FFF4E5] text-[#B45309] rounded-full px-2 py-0.5 text-[11px] font-medium">
          <Signal className="w-3 h-3" strokeWidth={1.5} /> Nuovo segnale
        </span>
      )}
    </div>
  );
}

function OppCard({ o, onOpen, onDelete, draggable, onDragStart, onDragEnd, dragging }) {
  return (
    <div
      data-testid={`opp-card-${o.id}`}
      draggable={draggable}
      onDragStart={(e) => onDragStart?.(e, o)}
      onDragEnd={onDragEnd}
      onClick={(e) => onOpen(e, o.id)}
      onAuxClick={(e) => { if (e.button === 1) onOpen(e, o.id); }}
      className={`group bg-white border border-line border-l-[3px] ${STAGE_COLORS[o.stage] || "border-l-faint"} rounded-[8px] p-3 cursor-pointer hover:border-brand transition-colors ${dragging ? "opacity-40" : ""}`}
    >
      <div className="flex items-start justify-between gap-2 mb-1.5">
        <div className="flex items-start gap-1.5 min-w-0">
          {draggable && <GripVertical className="w-4 h-4 text-faint shrink-0 mt-0.5 cursor-grab" strokeWidth={1.5} />}
          <a
            href={`/opportunita/${o.id}`}
            onClick={(e) => {
              e.stopPropagation();
              if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
              e.preventDefault();
              onOpen(e, o.id);
            }}
            onAuxClick={(e) => e.stopPropagation()}
            draggable={false}
            className="text-sm font-semibold text-ink leading-tight truncate hover:text-brand transition-colors"
          >{o.company_name}</a>
        </div>
        <button
          data-testid={`delete-opp-${o.id}`}
          onClick={(e) => { e.stopPropagation(); onDelete(o); }}
          className="text-faint hover:text-destructive transition-colors opacity-0 group-hover:opacity-100 shrink-0"
          title="Elimina opportunità"
        >
          <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
        </button>
      </div>
      <Badges o={o} />
      {o.next_task && <div className="mt-2"><TaskBadge task={o.next_task} /></div>}
      <p className="text-xs text-subtle mt-2 truncate">{o.position_name}</p>
      <div className="flex items-center justify-between mt-2 pt-2 border-t border-line">
        <div className="flex items-center gap-2 text-[11px] text-faint">
          <span>{o.signal_count} segnali</span>
          <span>·</span>
          <span>{o.dm_count} DM</span>
        </div>
        {o.assigned_to_name ? (
          <span className="text-[11px] text-subtle font-medium">{o.assigned_to_name}</span>
        ) : (
          <span className="text-[11px] text-faint">Non assegnato</span>
        )}
      </div>
    </div>
  );
}

export default function Feed() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [view, setView] = useState(() => localStorage.getItem("lovac_opp_view") || "kanban");
  const [stages, setStages] = useState([]);
  const [opps, setOpps] = useState([]);
  const [positions, setPositions] = useState([]);
  const [users, setUsers] = useState([]);
  const [filters, setFilters] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem("lovac_opp_filters"));
      if (saved && typeof saved === "object") return { position_id: "", assigned_to: "", badge: "", esito: "aperto", stage: "", loss_reason: "", ...saved };
    } catch { /* ignore */ }
    return { position_id: "", assigned_to: "", badge: "", esito: "aperto", stage: "", loss_reason: "" };
  });
  const [loading, setLoading] = useState(true);
  const [dragId, setDragId] = useState(null);
  const [overStage, setOverStage] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [campaignModal, setCampaignModal] = useState({ open: false, mode: "new", targetId: "", existing: [], name: "", startAt: "", autoEnrich: true, skipPregen: false, saving: false });
  const [lossReasons, setLossReasons] = useState([]);
  const [bulkLost, setBulkLost] = useState({ open: false, reason: "", note: "", saving: false });

  useEffect(() => { localStorage.setItem("lovac_opp_filters", JSON.stringify(filters)); }, [filters]);
  useEffect(() => { localStorage.setItem("lovac_opp_view", view); }, [view]);

  const load = useCallback(() => {
    const params = {};
    Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
    setLoading(true);
    setSelected(new Set());
    api.get("/opportunities", { params }).then((r) => setOpps(r.data)).finally(() => setLoading(false));
  }, [filters]);

  useEffect(() => {
    api.get("/pipeline/stages").then((r) => setStages(r.data));
    api.get("/pipeline/loss-reasons").then((r) => setLossReasons(r.data)).catch(() => {});
    api.get("/positions").then((r) => setPositions(r.data)).catch(() => {});
    api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
  }, [user]);

  useEffect(() => { load(); }, [load]);

  const oppHref = (id) => `/opportunita/${id}`;
  const open = (e, id) => {
    if (e && (e.metaKey || e.ctrlKey || e.button === 1)) {
      window.open(oppHref(id), "_blank");
      return;
    }
    navigate(oppHref(id));
  };

  const deleteOpp = async (o) => {
    if (!window.confirm(`Eliminare definitivamente l'opportunità di "${o.company_name}"? L'azione non è reversibile.`)) return;
    try {
      await api.delete(`/opportunities/${o.id}`);
      setOpps((prev) => prev.filter((x) => x.id !== o.id));
      toast.success("Opportunità eliminata");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore eliminazione");
    }
  };

  const toggleSel = (id) => setSelected((prev) => {
    const n = new Set(prev);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });
  const toggleAll = () => setSelected((prev) => prev.size === opps.length ? new Set() : new Set(opps.map((o) => o.id)));
  const bulkDelete = async () => {
    const ids = [...selected];
    if (!ids.length) return;
    if (!window.confirm(`Eliminare definitivamente ${ids.length} opportunità? L'azione non è reversibile.`)) return;
    try {
      const { data } = await api.post("/opportunities/bulk-delete", { ids });
      toast.success(`${data.deleted} opportunità eliminate`);
      setSelected(new Set());
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const bulkAssign = async (val) => {
    const ids = [...selected];
    if (!ids.length) return;
    const userId = val === "__unassign" ? null : val;
    try {
      const { data } = await api.post("/opportunities/bulk-assign", { ids, user_id: userId });
      toast.success(`${data.updated} opportunità ${userId ? "riassegnate" : "liberate"}`);
      setSelected(new Set());
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const openCampaignModal = async () => {
    setCampaignModal((m) => ({ ...m, open: true, mode: "new", targetId: "", existing: [] }));
    try {
      const { data } = await api.get("/campaigns");
      const active = data.filter((c) => c.status !== "stopped");
      setCampaignModal((m) => ({ ...m, existing: active }));
    } catch { /* ignore */ }
  };

  const createCampaign = async () => {
    const ids = [...selected];
    if (!ids.length) return;
    if (campaignModal.mode === "existing" && !campaignModal.targetId) return;
    if (campaignModal.mode === "new" && !campaignModal.name.trim()) return;
    setCampaignModal((m) => ({ ...m, saving: true }));
    try {
      if (campaignModal.mode === "existing") {
        const { data } = await api.post(`/campaigns/${campaignModal.targetId}/add`, { opportunity_ids: ids });
        toast.success(`${data.added} opportunità aggiunte${data.duplicates ? ` (${data.duplicates} già presenti)` : ""}`);
      } else {
        await api.post("/campaigns", {
          name: campaignModal.name.trim(),
          opportunity_ids: ids,
          start_at: campaignModal.startAt ? new Date(campaignModal.startAt).toISOString() : null,
          auto_enrich: campaignModal.autoEnrich,
          skip_pregenerated: campaignModal.skipPregen,
        });
        toast.success("Campagna creata");
      }
      setCampaignModal({ open: false, mode: "new", targetId: "", existing: [], name: "", startAt: "", autoEnrich: true, skipPregen: false, saving: false });
      setSelected(new Set());
      navigate("/campagne");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setCampaignModal((m) => ({ ...m, saving: false }));
    }
  };
  const bulkAddFollower = async (userId) => {
    const ids = [...selected];
    if (!ids.length || !userId) return;
    try {
      const { data } = await api.post("/opportunities/bulk-add-follower", { ids, user_id: userId });
      toast.success(`Follower aggiunto a ${data.updated} opportunità`);
      setSelected(new Set());
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const bulkStage = async (stage) => {
    const ids = [...selected];
    if (!ids.length || !stage) return;
    try {
      const { data } = await api.post("/opportunities/bulk-stage", { ids, stage });
      if (data.updated === 0 && data.skipped_closed > 0) {
        toast.warning(`Nessuna spostata: ${data.skipped_closed} opportunità sono chiuse (Vinte/Perse) e non modificabili. Riaprile prima con "Cambia stato → Riapri".`);
      } else {
        let msg = `${data.updated} opportunità spostate di fase`;
        if (data.skipped_closed > 0) msg += ` · ${data.skipped_closed} saltate perché chiuse`;
        toast.success(msg);
      }
      setSelected(new Set());
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const bulkEsito = async (esito) => {
    const ids = [...selected];
    if (!ids.length || !esito) return;
    if (esito === "perso") { setBulkLost({ open: true, reason: "", note: "", saving: false }); return; }
    try {
      const { data } = await api.post("/opportunities/bulk-esito", { ids, esito });
      toast.success(`${data.updated} opportunità ${esito === "aperto" ? "riaperte" : "segnate come Vinte"}`);
      setSelected(new Set());
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const confirmBulkLost = async () => {
    const ids = [...selected];
    if (!bulkLost.reason) return toast.error("Seleziona un motivo");
    setBulkLost((m) => ({ ...m, saving: true }));
    try {
      const { data } = await api.post("/opportunities/bulk-esito", { ids, esito: "perso", reason: bulkLost.reason, note: bulkLost.note });
      toast.success(`${data.updated} opportunità segnate come Perse`);
      setBulkLost({ open: false, reason: "", note: "", saving: false });
      setSelected(new Set());
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setBulkLost((m) => ({ ...m, saving: false }));
    }
  };

  const onDragStart = (e, o) => {
    setDragId(o.id);
    e.dataTransfer.effectAllowed = "move";
  };
  const onDragEnd = () => { setDragId(null); setOverStage(null); };

  const onDrop = async (stageKey) => {
    const o = opps.find((x) => x.id === dragId);
    setOverStage(null);
    setDragId(null);
    if (!o || o.stage === stageKey) return;
    const prevStage = o.stage;
    setOpps((prev) => prev.map((x) => (x.id === o.id ? { ...x, stage: stageKey } : x)));
    try {
      await api.post(`/opportunities/${o.id}/stage`, { stage: stageKey });
      load();
    } catch (e) {
      setOpps((prev) => prev.map((x) => (x.id === o.id ? { ...x, stage: prevStage } : x)));
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div className="flex flex-col gap-6 min-w-0">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-ink">Opportunità</h1>
          <p className="text-xs text-subtle mt-0.5">{opps.length} opportunità {filters.esito === "aperto" ? "aperte" : ""}</p>
        </div>
        <div className="flex items-center gap-1 bg-white border border-line rounded-[6px] p-1">
          <button
            data-testid="view-kanban"
            onClick={() => setView("kanban")}
            className={`p-1.5 rounded-[4px] transition-colors ${view === "kanban" ? "bg-brand-soft text-brand-dark" : "text-subtle hover:bg-background"}`}
          >
            <LayoutGrid className="w-4 h-4" strokeWidth={1.5} />
          </button>
          <button
            data-testid="view-list"
            onClick={() => setView("list")}
            className={`p-1.5 rounded-[4px] transition-colors ${view === "list" ? "bg-brand-soft text-brand-dark" : "text-subtle hover:bg-background"}`}
          >
            <List className="w-4 h-4" strokeWidth={1.5} />
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 bg-white border border-line rounded-[8px] p-3">
        <Filter className="w-4 h-4 text-subtle" strokeWidth={1.5} />
        <FilterSelect
          testid="filter-esito" value={filters.esito} placeholder="Esito"
          onChange={(v) => setFilters({ ...filters, esito: v })}
          options={[{ value: "aperto", label: "Aperte" }, { value: "vinto", label: "Vinte" }, { value: "perso", label: "Perse" }, { value: "__all", label: "Tutte" }]}
        />
        <FilterSelect
          testid="filter-position" value={filters.position_id} placeholder="Posizione"
          onChange={(v) => setFilters({ ...filters, position_id: v })}
          options={[{ value: "__all", label: "Tutte le posizioni" }, ...positions.map((p) => ({ value: p.id, label: p.name }))]}
        />
        {user?.role === "admin" && (
          <FilterSelect
            testid="filter-assigned" value={filters.assigned_to} placeholder="Sales"
            onChange={(v) => setFilters({ ...filters, assigned_to: v })}
            options={[{ value: "__all", label: "Tutti i sales" }, { value: "unassigned", label: "Non assegnate" }, ...users.map((u) => ({ value: u.id, label: u.name }))]}
          />
        )}
        <FilterSelect
          testid="filter-badge" value={filters.badge} placeholder="Badge"
          onChange={(v) => setFilters({ ...filters, badge: v })}
          options={[{ value: "__all", label: "Tutti i badge" }, { value: "reactivation", label: "Riattivate" }, { value: "new_signal", label: "Nuovo segnale" }]}
        />
        {view === "list" && (
          <FilterSelect
            testid="filter-stage" value={filters.stage} placeholder="Fase"
            onChange={(v) => setFilters({ ...filters, stage: v })}
            options={[{ value: "__all", label: "Tutte le fasi" }, ...stages.map((s) => ({ value: s.key, label: s.name }))]}
          />
        )}
        {filters.esito === "perso" && (
          <FilterSelect
            testid="filter-loss-reason" value={filters.loss_reason} placeholder="Motivo perdita"
            onChange={(v) => setFilters({ ...filters, loss_reason: v })}
            options={[{ value: "__all", label: "Tutti i motivi" }, ...lossReasons.map((r) => ({ value: r.label, label: r.label }))]}
          />
        )}
      </div>

      {loading ? (
        <p className="text-sm text-subtle">Caricamento…</p>
      ) : view === "kanban" ? (
        <div className="flex gap-4 overflow-x-auto pb-4">
          {stages.map((s) => {
            const list = opps.filter((o) => o.stage === s.key);
            return (
              <div
                key={s.id}
                data-testid={`stage-col-${s.key}`}
                className="w-72 shrink-0"
                onDragOver={(e) => { if (dragId) { e.preventDefault(); setOverStage(s.key); } }}
                onDragLeave={(e) => { if (e.currentTarget === e.target) setOverStage(null); }}
                onDrop={() => onDrop(s.key)}
              >
                <div className="flex items-center justify-between mb-3 px-1">
                  <p className="text-sm font-semibold text-ink">{s.name}</p>
                  <span className="text-xs text-faint bg-white border border-line rounded-full px-2 py-0.5">{list.length}</span>
                </div>
                <div className={`flex flex-col gap-2 min-h-[80px] rounded-[8px] transition-colors ${overStage === s.key ? "bg-brand-soft/60 ring-2 ring-brand ring-inset p-1" : ""}`}>
                  {list.map((o) => (
                    <OppCard
                      key={o.id}
                      o={o}
                      onOpen={open}
                      onDelete={deleteOpp}
                      draggable={o.esito === "aperto"}
                      onDragStart={onDragStart}
                      onDragEnd={onDragEnd}
                      dragging={dragId === o.id}
                    />
                  ))}
                  {list.length === 0 && <p className="text-xs text-faint px-1 py-4 text-center border border-dashed border-line rounded-[8px]">Trascina qui</p>}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="bg-white border border-line rounded-[8px] overflow-hidden">
          {selected.size > 0 && (
            <div data-testid="bulk-action-bar" className="flex items-center justify-between gap-3 bg-brand-soft border-b border-line px-4 py-2.5">
              <span className="text-sm font-medium text-brand-dark">{selected.size} selezionate</span>
              <div className="flex items-center gap-2">
                <select
                  data-testid="bulk-assign-select"
                  defaultValue=""
                  onChange={(e) => { bulkAssign(e.target.value); e.target.value = ""; }}
                  className="text-xs bg-white border border-line rounded-[6px] px-2 py-1.5 cursor-pointer focus:outline-none focus:ring-2 focus:ring-brand"
                >
                  <option value="" disabled>Assegna a…</option>
                  <option value="__unassign">Rimuovi assegnazione</option>
                  {users.map((u) => (
                    <option key={u.id} value={u.id}>{u.name}</option>
                  ))}
                </select>
                <select
                  data-testid="bulk-follower-select"
                  defaultValue=""
                  onChange={(e) => { bulkAddFollower(e.target.value); e.target.value = ""; }}
                  className="text-xs bg-white border border-line rounded-[6px] px-2 py-1.5 cursor-pointer focus:outline-none focus:ring-2 focus:ring-brand"
                >
                  <option value="" disabled>Aggiungi follower…</option>
                  {users.map((u) => (
                    <option key={u.id} value={u.id}>{u.name}</option>
                  ))}
                </select>
                <select
                  data-testid="bulk-stage-select"
                  defaultValue=""
                  onChange={(e) => { bulkStage(e.target.value); e.target.value = ""; }}
                  className="text-xs bg-white border border-line rounded-[6px] px-2 py-1.5 cursor-pointer focus:outline-none focus:ring-2 focus:ring-brand"
                >
                  <option value="" disabled>Sposta in fase…</option>
                  {stages.map((s) => (
                    <option key={s.key} value={s.key}>{s.name}</option>
                  ))}
                </select>
                <select
                  data-testid="bulk-esito-select"
                  defaultValue=""
                  onChange={(e) => { bulkEsito(e.target.value); e.target.value = ""; }}
                  className="text-xs bg-white border border-line rounded-[6px] px-2 py-1.5 cursor-pointer focus:outline-none focus:ring-2 focus:ring-brand"
                >
                  <option value="" disabled>Cambia stato…</option>
                  <option value="aperto">Riapri (Aperto)</option>
                  <option value="vinto">Segna Vinto</option>
                  <option value="perso">Segna Perso</option>
                </select>
                <button data-testid="bulk-clear" onClick={() => setSelected(new Set())} className="text-xs text-subtle hover:text-ink transition-colors">Deseleziona</button>
                {user?.role === "admin" && (
                  <button data-testid="bulk-campaign-button" onClick={openCampaignModal} className="flex items-center gap-1.5 bg-brand text-ink text-xs font-medium rounded-[6px] px-3 py-1.5 hover:bg-brand-hover transition-colors">
                    <Megaphone className="w-3.5 h-3.5" strokeWidth={1.5} /> Crea campagna
                  </button>
                )}
                <button data-testid="bulk-delete-button" onClick={bulkDelete} className="flex items-center gap-1.5 bg-destructive text-white text-xs font-medium rounded-[6px] px-3 py-1.5 hover:opacity-90 transition-opacity">
                  <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} /> Elimina selezionate
                </button>
              </div>
            </div>
          )}
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-line text-left text-xs text-subtle">
                <th className="px-4 py-2 w-10">
                  <input data-testid="bulk-select-all" type="checkbox" checked={opps.length > 0 && selected.size === opps.length} onChange={toggleAll} className="cursor-pointer accent-[#00E5A0]" />
                </th>
                <th className="px-4 py-2 font-semibold">Azienda</th>
                <th className="px-4 py-2 font-semibold">Posizione</th>
                <th className="px-4 py-2 font-semibold">Fase</th>
                <th className="px-4 py-2 font-semibold">Task</th>
                <th className="px-4 py-2 font-semibold">Sales</th>
                <th className="px-4 py-2 font-semibold">Badge</th>
                <th className="px-4 py-2 font-semibold"></th>
              </tr>
            </thead>
            <tbody>
              {opps.map((o) => (
                <tr key={o.id} data-testid={`opp-row-${o.id}`} onClick={(e) => open(e, o.id)} onAuxClick={(e) => { if (e.button === 1) open(e, o.id); }} className={`border-b border-line last:border-0 hover:bg-background cursor-pointer transition-colors ${selected.has(o.id) ? "bg-brand-soft/40" : ""}`}>
                  <td className="px-4 py-2.5" onClick={(e) => e.stopPropagation()}>
                    <input data-testid={`bulk-select-${o.id}`} type="checkbox" checked={selected.has(o.id)} onChange={() => toggleSel(o.id)} className="cursor-pointer accent-[#00E5A0]" />
                  </td>
                  <td className="px-4 py-2.5 font-medium">
                    <a
                      href={`/opportunita/${o.id}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
                        e.preventDefault();
                        open(e, o.id);
                      }}
                      onAuxClick={(e) => e.stopPropagation()}
                      className="hover:text-brand transition-colors"
                    >{o.company_name}</a>
                  </td>
                  <td className="px-4 py-2.5 text-subtle text-xs">{o.position_name}</td>
                  <td className="px-4 py-2.5">{stages.find((s) => s.key === o.stage)?.name || o.stage}</td>
                  <td className="px-4 py-2.5">{o.next_task ? <TaskBadge task={o.next_task} /> : <span className="text-faint text-xs">—</span>}</td>
                  <td className="px-4 py-2.5 text-xs">{o.assigned_to_name || <span className="text-faint">—</span>}</td>
                  <td className="px-4 py-2.5"><Badges o={o} /></td>
                  <td className="px-4 py-2.5 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button data-testid={`delete-opp-row-${o.id}`} onClick={(e) => { e.stopPropagation(); deleteOpp(o); }} className="text-faint hover:text-destructive transition-colors" title="Elimina opportunità">
                        <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {opps.length === 0 && <p className="text-sm text-faint text-center py-8">Nessuna opportunità</p>}
        </div>
      )}

      {campaignModal.open && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => !campaignModal.saving && setCampaignModal((m) => ({ ...m, open: false }))}>
          <div data-testid="campaign-modal" className="bg-white rounded-[10px] w-full max-w-md p-5 flex flex-col gap-4" onClick={(e) => e.stopPropagation()}>
            <div>
              <h3 className="text-lg font-semibold text-ink flex items-center gap-2"><Megaphone className="w-5 h-5 text-brand-dark" strokeWidth={1.5} /> Nuova campagna email</h3>
              <p className="text-xs text-subtle mt-1">{selected.size} opportunità selezionate. Le email partiranno dall'account Google dell'assegnatario di ciascuna.</p>
            </div>
            {campaignModal.existing.length > 0 && (
              <div className="flex gap-2 bg-background rounded-[6px] p-1">
                <button data-testid="campaign-mode-new" onClick={() => setCampaignModal((m) => ({ ...m, mode: "new" }))} className={`flex-1 text-xs font-medium rounded-[5px] py-1.5 transition-colors ${campaignModal.mode === "new" ? "bg-white text-ink shadow-sm" : "text-subtle"}`}>Nuova campagna</button>
                <button data-testid="campaign-mode-existing" onClick={() => setCampaignModal((m) => ({ ...m, mode: "existing" }))} className={`flex-1 text-xs font-medium rounded-[5px] py-1.5 transition-colors ${campaignModal.mode === "existing" ? "bg-white text-ink shadow-sm" : "text-subtle"}`}>Aggiungi a esistente</button>
              </div>
            )}
            {campaignModal.mode === "existing" ? (
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-subtle">Campagna</label>
                <select data-testid="campaign-target" value={campaignModal.targetId} onChange={(e) => setCampaignModal((m) => ({ ...m, targetId: e.target.value }))} className="bg-white border border-line rounded-[6px] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand">
                  <option value="">Seleziona una campagna…</option>
                  {campaignModal.existing.map((c) => (
                    <option key={c.id} value={c.id}>{c.name} ({(c.stats || {}).total || 0} opp.)</option>
                  ))}
                </select>
                <p className="text-[11px] text-faint mt-1">Le opportunità già presenti nella campagna verranno ignorate.</p>
              </div>
            ) : (
              <>
                <div className="flex flex-col gap-1">
                  <label className="text-xs font-medium text-subtle">Nome campagna</label>
                  <input data-testid="campaign-name" value={campaignModal.name} onChange={(e) => setCampaignModal((m) => ({ ...m, name: e.target.value }))} placeholder="Es. Edilizia — Marzo" className="bg-white border border-line rounded-[6px] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand" />
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-xs font-medium text-subtle">Inizio invio (lascia vuoto per partire subito)</label>
                  <input data-testid="campaign-start" type="datetime-local" value={campaignModal.startAt} onChange={(e) => setCampaignModal((m) => ({ ...m, startAt: e.target.value }))} className="bg-white border border-line rounded-[6px] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand" />
                </div>
                <label className="flex items-center gap-2 text-sm text-ink cursor-pointer">
                  <input data-testid="campaign-enrich" type="checkbox" checked={campaignModal.autoEnrich} onChange={(e) => setCampaignModal((m) => ({ ...m, autoEnrich: e.target.checked }))} className="accent-[#00E5A0]" />
                  Enrichment automatico dei DM senza email (consuma crediti)
                </label>
                <label className="flex items-center gap-2 text-sm text-ink cursor-pointer">
                  <input data-testid="campaign-skip-pregen" type="checkbox" checked={campaignModal.skipPregen} onChange={(e) => setCampaignModal((m) => ({ ...m, skipPregen: e.target.checked }))} className="accent-[#00E5A0]" />
                  Escludi opportunità con email già generata manualmente
                </label>
              </>
            )}
            {(() => {
              const unassignedCount = opps.filter((o) => selected.has(o.id) && !o.assigned_to).length;
              if (unassignedCount === 0) return null;
              return (
                <div data-testid="campaign-unassigned-warning" className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-[6px] px-3 py-2">
                  <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" strokeWidth={1.5} />
                  <p className="text-xs text-amber-800">Attenzione: {unassignedCount} {unassignedCount === 1 ? "opportunità non è assegnata" : `delle ${selected.size} opportunità selezionate non sono assegnate`} e {unassignedCount === 1 ? "verrà saltata" : "verranno saltate"}.</p>
                </div>
              );
            })()}
            <p className="text-[11px] text-faint">Invio nei giorni/orari e con i limiti impostati in Amministrazione. Le opportunità non assegnate o senza account Google collegato verranno saltate.</p>
            <div className="flex justify-end gap-2 pt-1">
              <button data-testid="campaign-cancel" onClick={() => setCampaignModal((m) => ({ ...m, open: false }))} disabled={campaignModal.saving} className="text-sm text-subtle hover:text-ink px-3 py-2">Annulla</button>
              <button data-testid="campaign-submit" onClick={createCampaign} disabled={campaignModal.saving || (campaignModal.mode === "new" ? !campaignModal.name.trim() : !campaignModal.targetId)} className="bg-brand text-ink font-medium rounded-[6px] px-4 py-2 text-sm hover:bg-brand-hover disabled:opacity-50 transition-colors">
                {campaignModal.saving ? "Salvo…" : campaignModal.mode === "existing" ? "Aggiungi" : "Crea e avvia"}
              </button>
            </div>
          </div>
        </div>
      )}

      {bulkLost.open && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => !bulkLost.saving && setBulkLost((m) => ({ ...m, open: false }))}>
          <div data-testid="bulk-lost-modal" className="bg-white rounded-[10px] w-full max-w-md p-5 flex flex-col gap-4" onClick={(e) => e.stopPropagation()}>
            <div>
              <h3 className="text-lg font-semibold text-ink">Segna come Perse</h3>
              <p className="text-xs text-subtle mt-1">{selected.size} opportunità selezionate. Solo le opportunità aperte verranno chiuse.</p>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-subtle">Motivo (obbligatorio)</label>
              <select data-testid="bulk-lost-reason" value={bulkLost.reason} onChange={(e) => setBulkLost((m) => ({ ...m, reason: e.target.value }))} className="bg-white border border-line rounded-[6px] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand">
                <option value="">Seleziona motivo…</option>
                {lossReasons.map((r) => <option key={r.id} value={r.label}>{r.label}</option>)}
              </select>
            </div>
            <textarea data-testid="bulk-lost-note" value={bulkLost.note} onChange={(e) => setBulkLost((m) => ({ ...m, note: e.target.value }))} rows={2} placeholder="Nota (opzionale)" className="w-full bg-white border border-line rounded-[6px] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand" />
            <div className="flex justify-end gap-2 pt-1">
              <button data-testid="bulk-lost-cancel" onClick={() => setBulkLost((m) => ({ ...m, open: false }))} disabled={bulkLost.saving} className="text-sm text-subtle hover:text-ink px-3 py-2">Annulla</button>
              <button data-testid="bulk-lost-confirm" onClick={confirmBulkLost} disabled={bulkLost.saving || !bulkLost.reason} className="bg-destructive text-white font-medium rounded-[6px] px-4 py-2 text-sm hover:opacity-90 disabled:opacity-50 transition-opacity">
                {bulkLost.saving ? "Salvo…" : "Conferma perdita"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function FilterSelect({ value, onChange, options, placeholder, testid }) {
  return (
    <Select value={value || "__all"} onValueChange={(v) => onChange(v === "__all" ? "" : v)}>
      <SelectTrigger data-testid={testid} className="w-auto min-w-[150px] h-9 bg-white border-line rounded-[6px] text-sm">
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent className="bg-white border-line">
        {options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}
